import React, { memo, useCallback, useState } from 'react';
import { ContextMenu, MenuItem, ContextMenuTrigger } from 'react-contextmenu';
import PropTypes from 'prop-types';
import { Map, List } from 'immutable';

import FormGroup from '../FormGroup';
import { InputProps } from '../AutoComplete';
import AutoCompleteContainer from '../../../containers/AutoComplete';
import { usePopupContext } from '../../../contexts/PopupContext';
import { IconButton } from '../IconButton';

import style from './style.module.css';

const emptyMap = Map();

const EmailTemplateMenuItems = ({ data, ...rest }) => {
  if (!data) return null;

  const { email, onOpenEmail, onEditEmailTemplate, onCopyEmailTemplate } = rest;
  const menu = data.map(template => (
    <MenuItem key={template.get('id')} onClick={() => onOpenEmail(email, template)}>
      <div className={style.menuItem}>
        <span className={style.menuItemLabel}>{template.get('label')}</span>
        <div className={style.menuItemActions}>
          <IconButton icon="copy" onClick={event => onCopyEmailTemplate(event, template)} size="sm" title="Copy" />
          <IconButton icon="edit" onClick={event => onEditEmailTemplate(event, template)} size="sm" title="Edit" />
        </div>
      </div>
    </MenuItem>
  ));

  return <div className={style.templates}>{menu}</div>;
};

/**
 * Email templates context menu wrapper component.
 *
 * @param {object} props Props.
 * @param {Map} props.companyInfo Company data.
 * @param {string} props.email Email.
 * @param {List} props.emailTemplates Email templates.
 * @param {Function} props.fetchEmailTemplates Fetch email templates by suggested users.
 * @param {Function} props.findDirectors Find user by suggestions search pattern.
 * @param {string} props.id Context menu ID.
 * @param {Function} props.onOpenEmail Handle email link open.
 * @param {string} props.templateType Template type ("company | executive").
 */
const EmailTemplatesContextMenuWrapper = ({
  children,
  companyInfo,
  email,
  emailTemplates,
  fetchEmailTemplates,
  findDirectors,
  id,
  onOpenEmail,
  templateType,
}) => {
  const { openPopup } = usePopupContext();
  const [emailTemplateValue, setEmailTemplateValue] = useState(companyInfo && companyInfo.get('suggestDirector'));
  const [userId, setUserId] = useState(companyInfo && companyInfo.getIn(['recordOwnerId', 'value']));

  const handleSuggestChange = useCallback(
    ({ name, value }) => {
      if (name === 'suggestDirector') {
        setEmailTemplateValue(value);
      }

      if (name === 'recordOwnerId') {
        fetchEmailTemplates({ userId: value });
        setUserId(value);
      }
    },
    [fetchEmailTemplates],
  );

  const handleAddEmailTemplate = useCallback(() => {
    openPopup('AddEmailTemplatePopup', {
      headerTitle: 'Create email template',
      userId,
      isNew: true,
      emailTemplate: emptyMap,
      templateType,
    });
  }, [openPopup, userId, templateType]);

  const handleEditEmailTemplate = useCallback(
    (event, emailTemplate) => {
      event.stopPropagation();

      openPopup('AddEmailTemplatePopup', {
        headerTitle: 'Change email template',
        userId,
        isNew: false,
        emailTemplate,
        templateType,
      });
    },
    [openPopup, userId, templateType],
  );

  const handleCopyEmailTemplate = useCallback(
    (event, emailTemplate) => {
      event.stopPropagation();

      openPopup('AddEmailTemplatePopup', {
        headerTitle: 'Create email template - Copy',
        userId,
        isNew: true,
        emailTemplate: emailTemplate.delete('id').set('label', `${emailTemplate.get('label')} - Copy`),
        templateType,
      });
    },
    [openPopup, userId, templateType],
  );

  const handleShowMenu = useCallback(async () => {
    const newEmailTemplateValue = companyInfo.get('suggestDirector');
    const newUserId = companyInfo.getIn(['recordOwnerId', 'value']);

    setEmailTemplateValue(newEmailTemplateValue);
    setUserId(newUserId);

    if (newUserId) {
      fetchEmailTemplates({ userId: newUserId });
    }
  }, [companyInfo, fetchEmailTemplates]);

  return (
    <>
      <ContextMenuTrigger holdToDisplay={0} id={id}>
        {children}
      </ContextMenuTrigger>

      <ContextMenu hideOnLeave={false} id={id} onShow={handleShowMenu}>
        <MenuItem onClick={event => event.stopPropagation()} preventClose>
          <FormGroup className="form-inline-block">
            <InputProps className="form-control input-sm bounded" name="director" placeholder="Director">
              <AutoCompleteContainer
                idName="recordOwnerId"
                idValue="recordOwnerId"
                onChange={handleSuggestChange}
                onFind={findDirectors}
                suggestions={companyInfo.get('suggests')}
                value={emailTemplateValue}
                valueName="suggestDirector"
                appendToContainer
              />
            </InputProps>
          </FormGroup>
        </MenuItem>
        {userId && (
          <MenuItem onClick={handleAddEmailTemplate}>
            <a className="clickable">
              <b>Add New</b>
            </a>
          </MenuItem>
        )}
        <EmailTemplateMenuItems
          data={emailTemplates}
          email={email}
          onCopyEmailTemplate={handleCopyEmailTemplate}
          onEditEmailTemplate={handleEditEmailTemplate}
          onOpenEmail={onOpenEmail}
        />
      </ContextMenu>
    </>
  );
};

EmailTemplatesContextMenuWrapper.propTypes = {
  companyInfo: PropTypes.instanceOf(Map).isRequired,
  email: PropTypes.string.isRequired,
  emailTemplates: PropTypes.instanceOf(List).isRequired,
  fetchEmailTemplates: PropTypes.func.isRequired,
  findDirectors: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  onOpenEmail: PropTypes.func.isRequired,
  templateType: PropTypes.string.isRequired,
};

export { EmailTemplatesContextMenuWrapper };
export default memo(EmailTemplatesContextMenuWrapper);
